import React, { useState } from "react";
import {
    Box,
    Modal,
    Typography,
    Avatar,
    IconButton,
    Chip,
    TextField,
    Button,
    Divider,
} from "@mui/material";
import { Send, Close, Restaurant, OutlinedFlag, Inventory2Outlined, WarningAmberOutlined, HourglassEmptyRounded } from "@mui/icons-material";
import "./style.css";
import dayjs from "dayjs";
import { useFeedbackReplyMutation } from "pages/manageReviews/service";
import CustomTextArea from "components/Fileds/CustomTextArea";
import ReviewReportModal from "../ReviewReport/ReviewReport";

const emojiRatings = [
    { id: "1", label: "😡" },
    { id: "2", label: "😞" },
    { id: "3", label: "😐" },
    { id: "4", label: "😊" },
    { id: "5", label: "🤩" },
];

const options = [
    { label: "Good", value: "good" },
    { label: "Average", value: "average" },
    { label: "Poor", value: "poor" },
]

const ReviewDetailsModal = ({ open, handleClose, reviewData }) => {

    const closeModal = () => {
        setReplyText("");
        handleClose();
    };

    const [replyText, setReplyText] = useState("");
    const [reviewReportModal, setReviewReportModal] = useState(false)

    let parsedTags = [];
    if (Array.isArray(reviewData?.tags)) {
        parsedTags = reviewData.tags;
    } else if (typeof reviewData?.tags === "string") {
        try {
            const parsed = JSON.parse(reviewData.tags);
            parsedTags = Array.isArray(parsed) ? parsed : [parsed];
        } catch {
            // fallback to comma‑separated string
            parsedTags = reviewData.tags
                .split(",")
                .map((t) => t.trim())
                .filter(Boolean);
        }
    }

    const [feedbackReply, { isLoading }] = useFeedbackReplyMutation();

    const handleReply = async () => {
        try {
            await feedbackReply({ id: reviewData?.id, body: { reply: replyText } }).unwrap();
            setReplyText("");
            closeModal();
        } catch (error) {
            console.log(error);
        }
    };

    const handleReviewReportModal = () => {
        setReviewReportModal(true);
    }

    return (
        <Modal open={open} onClose={closeModal}>
            <Box className="review-details">
                {/* Header */}
                <Box className="review-details-header">
                    <Box>
                        <Typography className="review-details-title">
                            Review Details
                        </Typography>

                        <Typography className="review-details-subtitle">
                            View, reply, or report this review
                        </Typography>
                    </Box>

                    <IconButton onClick={closeModal}>
                        <Close />
                    </IconButton>
                </Box>

                {/* User Info */}
                <Box className="review-details-user-section">
                    <Box className="review-details-user-left">
                        <Avatar
                            className="review-details-avatar"
                            src={`${process.env.REACT_APP_API_ROUTE}/files/${reviewData?.customer?.profile_pic}` || ""}
                        >
                            {!reviewData?.customer?.profile_pic && reviewData?.customer?.name?.split(" ").map((word) => word[0]).join("").toUpperCase()}
                        </Avatar>
                        <Box>
                            <Typography className="review-details-user-name">
                                {reviewData?.customer?.name}
                            </Typography>

                            <Box className="review-details-meta">
                                <Typography className="review-details-date">
                                    {dayjs(reviewData?.created_at).format("YYYY-MM-DD HH:mm")}
                                </Typography>

                                <Chip
                                    label={`#${reviewData?.orderDetails?.order_id}`}
                                    className="order-chip"
                                />
                            </Box>
                        </Box>
                    </Box>

                    {!reviewData?.is_reported &&
                        <Button
                            variant="outlined"
                            startIcon={<OutlinedFlag />}
                            className="report-btn"
                            onClick={() => handleReviewReportModal()}
                        >
                            Report Review To Management
                        </Button>
                    }
                </Box>

                {/* Rating */}
                <Box className="rating-section">
                    <Box className="emoji-rating">
                        {emojiRatings.map((val) => (
                            <IconButton
                                key={val.id}
                                className={`emoji ${Number(reviewData?.rating) === Number(val.id) ? "active" : "inactive"
                                    }`}
                            >
                                {val.label}
                            </IconButton>
                        ))}
                    </Box>

                    <Typography className="rating-text">
                        {`Ratings: ${reviewData?.rating}/5`}
                    </Typography>
                </Box>

                {/* Categories */}
                <Box className="category-wrapper">
                    {/* Food Quality */}
                    <Box className="category-box">
                        <Box className="category-title-wrapper">
                            <Restaurant className="category-icon" />

                            <Typography className="category-title">
                                Food Quality
                            </Typography>
                        </Box>

                        <Box className="chip-group">
                            {options.map((item) => (
                                <Chip
                                    key={item}
                                    label={item.label}
                                    className={reviewData?.food_quality === item.value ? "active-chip" : "inactive-chip"}
                                />
                            ))}
                        </Box>
                    </Box>

                    {/* Packaging */}
                    <Box className="category-box">
                        <Box className="category-title-wrapper">
                            <Inventory2Outlined className="category-icon" />

                            <Typography className="category-title">
                                Packaging
                            </Typography>
                        </Box>

                        <Box className="chip-group">
                            {options.map((item) => (
                                <Chip
                                    key={item}
                                    label={item.label}
                                    className={reviewData?.packaging === item.value ? "active-chip" : "inactive-chip"}
                                />
                            ))}
                        </Box>
                    </Box>
                </Box>

                {/* Likes */}
                {parsedTags.length > 0 && (
                    <Box className="likes-section">
                        <Typography className="section-title">
                            What did they like?
                        </Typography>
                        <Box className="likes-wrapper">
                            {parsedTags.map((tag, index) => (
                                <Chip key={index} label={tag} className="tag-chip" />
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Feedback */}
                {reviewData?.description &&
                    <Box className="feedback-section">
                        <Typography className="section-title">
                            Additional Feedback
                        </Typography>

                        <Box className="feedback-box">
                            <Typography className="feedback-text">
                                {reviewData?.description}
                            </Typography>
                        </Box>
                    </Box>
                }

                {reviewData?.is_reported &&
                    <Box className="report-status-card">
                        <Box className="report-status-header">
                            <Box className="report-status-title">
                                <WarningAmberOutlined className="report-warning-icon" />
                                <Typography sx={{ color: "#dc2828", fontSize: "16px", fontWeight: 500 }}>
                                    Reported to Management
                                </Typography>
                            </Box>

                            <Chip
                                icon={<HourglassEmptyRounded />}
                                label="Pending Review"
                                className="pending-review-chip"
                            />
                        </Box>

                        <Typography className="report-reason">
                            Reason: {reviewData?.reported_text}
                        </Typography>
                    </Box>
                }

                {/* Reply Section */}
                {reviewData?.is_replied ?
                    <Box className="restaurant-reply-wrapper">
                        <Box className="restaurant-reply-header">
                            <Chip
                                label="Restaurant Reply"
                                className="reply-label-chip"
                            />

                            <Typography className="reply-date">
                                {dayjs(reviewData?.updated_at).format("YYYY-MM-DD")}
                            </Typography>
                        </Box>

                        <Typography className="restaurant-reply-text">
                            {reviewData?.replied_text}
                        </Typography>
                    </Box>
                    :
                    <Box Box className="reply-section">
                        <Divider sx={{ my: 2.5 }} />
                        <Typography className="reply-title">
                            Write a reply
                        </Typography>

                        <CustomTextArea
                            name="review-response"
                            minRows={4}
                            maxRows={5}
                            rows={4}
                            // fullWidth
                            placeholder="Type your response to this review..."
                            // className="reply-input"
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                        />

                        <Box className="reply-btn-wrapper">
                            <Button
                                disabled={!replyText || isLoading}
                                onClick={handleReply}
                                variant="contained"
                                startIcon={<Send />}
                                className="reply-btn"
                            >
                                Send Reply
                            </Button>
                        </Box>
                    </Box>
                }

                <ReviewReportModal
                    open={reviewReportModal}
                    handleClose={() => setReviewReportModal(false)}
                    reviewData={reviewData}
                />
            </Box>
        </Modal>
    );
};

export default ReviewDetailsModal;